import type {
  AddressDto,
  OrganizationDto,
  OrgSettingsUpdateDto,
} from "@strtmn/sds/types"
import type { UUID } from "@strtmn/sds/utils"
import { Cache } from "aws-amplify/utils"
import { isProd } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { clearCacheCurrentUserData } from "./userData"
import { getCacheKey, getFromApi, postToApi, putToApi } from "./utils"

export const createOrg = async (
  orgSettings: OrgSettingsUpdateDto
): Promise<boolean> => {
  console.log(`> createOrg()`, orgSettings)
  const apiName = "frontendApi"
  const path = `/orgs`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await postToApi(apiName, path, orgSettings)
    !isProd && console.debug("✅ createOrg: api response:", response)
  } catch (err) {
    console.error(`createOrg: Error creating org.`)
    console.log("createOrg: Error:", err)
    // !isProd && console.debug("❌ createOrg: throw")
    throw err
  }
  return true
}

export const updateOrgSettings = async (
  orgId: UUID,
  orgData: OrgSettingsUpdateDto
): Promise<boolean> => {
  console.log("> updateOrgSettings")
  const apiName = "frontendApi"
  const path = `/orgs/${orgId}/settings`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await putToApi(apiName, path, orgData)
    !isProd && console.debug("✅ updateOrgSettings: api response:", response)
  } catch (err) {
    console.error(
      `updateOrgSettings: Error updating org settings for ${orgId}.`
    )
    console.log("updateOrgSettings: Error:", err)
    // !isProd && console.debug("❌ updateOrgSettings: throw")
    throw err
  }
  await clearCacheOrgData(orgId)
  await clearCacheCurrentUserData()
  return true
}

export const listOrgs = async (): Promise<OrganizationDto[] | false> => {
  console.log(`> listOrgs()`)
  const apiName = "frontendApi"
  const path = `/orgs`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    console.log("listOrgs: Uncached. Loading from API...")
    let data: OrganizationDto[] | null = (await getFromApi(apiName, path))
      .json as OrganizationDto[]
    !isProd && console.debug("listOrgs: Fetched from api:", data)

    /** @todo: Move to backend (but that's on a different branch) */
    // Parse address if it's a string
    data.forEach((d) => {
      if (d.address && typeof d.address === "string") {
        try {
          const _address = JSON.parse(d.address)
          const _addressData: AddressDto = {
            street: _address.strasse,
            number: _address.hausnummer,
            zip: _address.plz,
            city: _address.ort,
            country: _address.land,
          }
          d.address = _addressData
        } catch (err) {
          console.warn("Could not parse address:", d.address)
        }
      }
    })

    // !isProd && console.debug("✅ listOrgs: data")
    return data
  } catch (err) {
    console.error("Error fetching Orgs data from api.")
    console.log("listOrgs: Error:", err)
    // !isProd && console.debug("❌ listOrgs: throw")
    throw err
  }
}

export const getById = async (
  orgId: UUID
): Promise<OrganizationDto | false> => {
  console.log(`> getById()`)
  const apiName = "frontendApi"
  const path = `/orgs/${orgId}`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    console.log("getById: Uncached. Loading from API...")
    let data: OrganizationDto | null = (await getFromApi(apiName, path))
      .json as OrganizationDto
    !isProd && console.debug("getById: Fetched from api:", data)

    /** @todo: Move to backend (but that's on a different branch) */
    // Parse address if it's a string
    if (data.address && typeof data.address === "string") {
      try {
        const _address = JSON.parse(data.address)
        const _addressData: AddressDto = {
          street: _address.strasse,
          number: _address.hausnummer,
          zip: _address.plz,
          city: _address.ort,
          country: _address.land,
        }
        data.address = _addressData
      } catch (err) {
        console.warn("getById: Could not parse address:", data.address)
      }
    }

    // !isProd && console.debug("✅ getById: data")
    return data
  } catch (err) {
    console.error("getById: Error fetching Org data from api.")
    console.log("getById: Error:", err)
    // !isProd && console.debug("❌ getById: throw")
    throw err
  }
}

export const sortOrgs = (orgs: OrganizationDto[]) => {
  console.debug(`> sortOrgs (${orgs.length} orgs)`)
  const pushO1ToBack = 1
  const pushO2ToBack = -1
  // orgs.forEach((o: OrganizationDto) => {
  //   o.adresseKurz =
  //     o.adresse && o.adresse.ort && o.adresse.strasse
  //       ? `${o.adresse.ort}, ${o.adresse.strasse}`
  //       : undefined
  // })
  // console.log('res: ', items)
  orgs.sort((o1: OrganizationDto, o2: OrganizationDto) => {
    // Sort by name
    if (o1.name < o2.name) {
      return pushO2ToBack
    }
    if (o1.name > o2.name) {
      return pushO1ToBack
    }

    // Sort by address
    const o1adresseKurz = !o1.address
      ? undefined
      : typeof o1.address === "string"
        ? o1.address
        : o1.address.city && o1.address.street
          ? `${o1.address.city}, ${o1.address.street}`
          : undefined
    const o2adresseKurz = !o1.address
      ? undefined
      : typeof o1.address === "string"
        ? o1.address
        : o1.address.city && o1.address.street
          ? `${o1.address.city}, ${o1.address.street}`
          : undefined
    if (o1adresseKurz && !o2adresseKurz) {
      return pushO2ToBack
    }
    if (!o1adresseKurz && o2adresseKurz) {
      return pushO1ToBack
    }
    if (o1adresseKurz && o2adresseKurz) {
      if (o1adresseKurz < o2adresseKurz) {
        return pushO2ToBack
      }
      if (o1adresseKurz > o2adresseKurz) {
        return pushO1ToBack
      }
    }
    return 0
  })
  return orgs
}

export const filterOrgs = (
  orgs: OrganizationDto[],
  filter: string | undefined
) => {
  if (!filter || !orgs) {
    return orgs
  }
  return orgs.filter((org) => {
    const _address = !org.address
      ? null
      : typeof org.address === "string"
        ? org.address
        : JSON.stringify(org.address)
    const haystack = [org.name, _address, org.ext_id].join("#").toLowerCase()
    return haystack.includes(filter)
  })
}

export const clearCacheOrgData = async (orgId: string): Promise<void> => {
  console.debug(`> clearCacheOrgData(${orgId})`)
  const apiName = "frontendApi"
  const path = `/orgs/${orgId}`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}
